import { useEffect, useState } from 'react';
import api from '../../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Zap, Clock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

// Catálogo singleton (1 sola llamada API por sesión)
let _opcionesCache = null;
let _opcionesPromise = null;
async function loadOpciones() {
  if (_opcionesCache) return _opcionesCache;
  if (_opcionesPromise) return _opcionesPromise;
  _opcionesPromise = api.get('/flujo-caja/cxp/formas-pago')
    .then((r) => { _opcionesCache = r.data?.items || []; return _opcionesCache; })
    .catch(() => []);
  return _opcionesPromise;
}

/**
 * Iter 234.d (Carlos) — Selector inline de forma de pago para UNA factura.
 *
 * Se usa por fila en CxPPanel. Muestra la forma de pago actual (o "Sin forma")
 * y un dropdown compacto para cambiarla. Si es instantánea, la factura sale
 * de la nómina al instante.
 *
 * Props:
 *   factura: {id, forma_pago?}
 *   onChanged: () => void  // refresh padre
 */
export default function RowFormaPagoSelect({ factura, onChanged, disabled }) {
  const [opciones, setOpciones] = useState([]);
  const [busy, setBusy] = useState(false);
  const current = factura?.forma_pago;
  const currentLabel = opciones.find((o) => o.key === current)?.label;

  useEffect(() => { loadOpciones().then(setOpciones); }, []);

  const apply = async (forma_pago) => {
    if (!factura?.id) return;
    setBusy(true);
    try {
      const res = await api.patch('/flujo-caja/cxp/forma-pago/bulk', {
        factura_ids: [factura.id],
        forma_pago,
      });
      const label = opciones.find((o) => o.key === forma_pago)?.label || forma_pago;
      toast.success(
        res.data?.instantanea
          ? `Marcada pagada (${label})`
          : `Forma "${label}" aplicada`
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const instantaneas = opciones.filter((o) => o.instantanea);
  const credito = opciones.filter((o) => !o.instantanea);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled || busy}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors disabled:opacity-50 ${
            current
              ? 'bg-sky-50 text-sky-700 border-sky-300 hover:bg-sky-100'
              : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
          }`}
          data-testid={`row-forma-pago-${factura?.id}`}
          title={current ? `Forma de pago: ${currentLabel}` : 'Asignar forma de pago'}
        >
          {currentLabel || 'Sin forma'} <ChevronDown className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-[11px] flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-amber-500" /> Salida inmediata
        </DropdownMenuLabel>
        {instantaneas.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onClick={() => apply(o.key)}
            className="text-xs"
            data-testid={`row-forma-pago-opt-${o.key}`}
          >
            {o.label} {current === o.key ? '✓' : ''}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-sky-500" /> Va a Nómina
        </DropdownMenuLabel>
        {credito.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onClick={() => apply(o.key)}
            className="text-xs"
            data-testid={`row-forma-pago-opt-${o.key}`}
          >
            {o.label} {current === o.key ? '✓' : ''}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
